import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AdlDetails, CareInstructions, DayToDayDetails } from '../sageHomeApi';

export interface CareNeedsState {
    careInstructions: CareInstructions | null;
}

const initialState: CareNeedsState = {
    careInstructions: null,
};

export const careInstructionsSlice = createSlice({
    name: 'careNeeds',
    initialState,
    reducers: {
        setCareInstructions: (state, action: PayloadAction<CareInstructions>) => {
            state.careInstructions = action.payload;
        },
        updateAdl: (state, action: PayloadAction<AdlDetails>) => {
            if (state.careInstructions?.adls) {
                const adlIndex = state.careInstructions.adls.findIndex(
                    (adl) => adl.name === action.payload.name
                );
                if (adlIndex !== -1) {
                    state.careInstructions.adls[adlIndex] = action.payload;
                }
            }
        },
        updateDayToDay: (state, action: PayloadAction<DayToDayDetails>) => {
            if (state.careInstructions?.dayToDays) {
                const dayToDayIndex = state.careInstructions.dayToDays.findIndex(
                    (dayToDay) => dayToDay.name === action.payload.name
                );
                if (dayToDayIndex !== -1) {
                    state.careInstructions.dayToDays[dayToDayIndex] = action.payload;
                }
            }
        },
        clearCareInstructions: (state) => {
            state.careInstructions = null;
        },
    },
    selectors: {
        selectCareInstructions: (state: CareNeedsState) => state.careInstructions,
        selectAdlByName: (state: CareNeedsState, name: string) =>
            state.careInstructions?.adls?.find((adl) => adl.name === name),
        selectDayToDayByName: (state: CareNeedsState, name: string) =>
            state.careInstructions?.dayToDays?.find((dayToDay) => dayToDay.name === name),
    },
});

export const { setCareInstructions, updateAdl, updateDayToDay, clearCareInstructions } =
    careInstructionsSlice.actions;

export const { selectCareInstructions, selectAdlByName, selectDayToDayByName } =
    careInstructionsSlice.selectors;

// Memoized selectors to prevent unnecessary re-renders due to empty arrays
export const selectAdls = createSelector(
    [selectCareInstructions],
    (careInstructions) => careInstructions?.adls || []
);

export const selectDayToDays = createSelector(
    [selectCareInstructions],
    (careInstructions) => careInstructions?.dayToDays || []
);

export default careInstructionsSlice.reducer;
